<template>
  <div>
    <HeroSection
      title="Edm8ker Trainer Academy"
      background-color="#4C4DDC"
      subtitle="Welcome to a community of educators shaping meaningful learning!"
      background-image="/images/trainer-academy/hero-banner.svg"
      background-image-mobile="/images/trainer-academy/hero-banner.svg"
      illustration-image="/images/trainer-academy/trainer-academy-illustration.svg"
      illustration-alt="Trainer Academy"
    />
    <div v-if="modules.length > 0 && !isLoading" class="content-section">
      <div v-if="averageProgress === 100" class="congratulations-banner">
        <div class="banner-icon">
          <img src="/images/icon/star-icon.svg" alt="Congratulations" />
        </div>
        <div class="banner-content">
          <p class="banner-text">
            Congratulations!!! You already finish all your module. You can
            download you certificate below.
          </p>
        </div>
      </div>
      <div class="content-row">
        <div class="course-description">
          <p class="description-text">
            <span class="text-regular">
              This short course will walk you through what it means to be an
              edm8ker trainer. From our teaching philosophy, to practical
              facilitation tips that help you bring lessons to life.
              <br aria-hidden="true" />
              By the end of this course, you should be equipped and ready to
              guide students in discovering the joy of making.
            </span>
          </p>
          <div class="certificate-notice">
            Be sure to save your certificate once finished for verification
            purposes!
          </div>
          <ActionButton
            variant="primary"
            :loading="isLoadingScormUrl || isDownloadingCertificate"
            loading-text="Loading..."
            :disabled="
              isLoadingScormUrl ||
              isDownloadingCertificate ||
              modules.length === 0
            "
            @click="handleContinueButtonClick"
            class="continue-button"
          >
            <template #icon-left>
              <DownloadIcon v-if="averageProgress === 100" color="white" />
              <EyeIcon v-else :width="24" :height="24" color="white" />
            </template>
            {{
              averageProgress === 100
                ? "Download E-Certificate"
                : averageProgress === 0
                  ? "Get Started"
                  : "Continue"
            }}
          </ActionButton>
        </div>
        <div class="progress-container">
          <div class="progress-info">
            <div class="progress-icon">
              <GraduationCapIcon :width="40" :height="40" color="#3B4648" />
            </div>
            <p class="progress-percentage">
              {{ Math.round(averageProgress) }}%
            </p>
            <p class="progress-label">Module Completed</p>
            <div class="progress-circle">
              <svg
                width="328"
                height="328"
                viewBox="0 0 328 328"
                class="progress-svg"
              >
                <circle
                  cx="164"
                  cy="164"
                  :r="circleRadius"
                  fill="none"
                  stroke="#BAC1CC"
                  :stroke-width="progressStrokeWidth"
                />
                <circle
                  cx="164"
                  cy="164"
                  :r="circleRadius"
                  fill="none"
                  stroke="#4C4DDC"
                  :stroke-width="progressStrokeWidth"
                  :stroke-dasharray="circumference"
                  :stroke-dashoffset="progressOffset"
                  transform="rotate(-90 164 164)"
                  class="progress-ring"
                />
              </svg>
            </div>
          </div>
        </div>
        <div class="modules-container">
          <div
            v-for="(module, index) in modules"
            :key="module.id || index"
            class="module-card"
            :class="{
              'module-active': module.progress > 0 && module.progress < 100,
              'module-completed': module.progress === 100,
            }"
          >
            <div class="module-info">
              <div class="module-details">
                <p class="module-number">Module {{ index + 1 }}</p>
                <p class="module-title">{{ module.title }}</p>
              </div>
              <button
                class="view-module-button"
                :class="{ 'button-disabled': isModuleDisabled(index) }"
                @click="viewModule(module.id)"
                :disabled="isModuleDisabled(index)"
              >
                <span class="button-text">View Module</span>
                <ArrowCircleIcon
                  :width="16.667"
                  :height="16.667"
                  :color="isModuleDisabled(index) ? '#CCCCCC' : '#003D6A'"
                />
              </button>
            </div>
            <div class="module-progress">
              <div class="progress-bar-container">
                <div
                  class="progress-bar-fill"
                  :style="{ width: module.progress + '%' }"
                ></div>
              </div>
              <p class="progress-text">{{ module.progress }}%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <LoadingState v-else-if="isLoading" message="Loading modules..." />
    <div v-else class="empty-state-container">
      <div class="empty-state">
        <div class="empty-state-icon">
          <GraduationCapIcon :width="80" :height="80" color="#BAC1CC" />
        </div>
        <h3 class="empty-state-title">No Modules Available Yet</h3>
        <p class="empty-state-message">
          We're currently preparing exciting training modules to help you excel
          as an edm8ker trainer. Check back soon for new content that will
          enhance your teaching journey!
        </p>
      </div>
    </div>

    <!-- SCORM Iframe Modal -->
    <div v-if="showScormIframe" class="scorm-iframe-modal">
      <div class="scorm-iframe-container">
        <div class="scorm-iframe-header">
          <v-btn @click="closeScormIframe" icon="mdi-close" size="small" />
        </div>
        <div v-if="isScormIframeLoading" class="scorm-iframe-loading">
          <div class="scorm-loader"></div>
          <p>Loading SCORM content...</p>
        </div>
        <iframe
          :src="scormIframeUrl"
          frameborder="0"
          allowfullscreen
          @load="scormIframeLoaded"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script setup>
import ActionButton from "@/components/common/ActionButton.vue";
import HeroSection from "@/components/common/HeroSection.vue";
import LoadingState from "@/components/common/LoadingState.vue";
import ArrowCircleIcon from "@/components/icons/ArrowCircleIcon.vue";
import DownloadIcon from "@/components/icons/DownloadIcon.vue";
import EyeIcon from "@/components/icons/EyeIcon.vue";
import GraduationCapIcon from "@/components/icons/GraduationCapIcon.vue";
import cookiesService from "@/services/cookiesServiceNoAwait";
import trainerAcademyService from "@/services/trainerAcademyService";
import { getApiError } from "@/utils/apiError";
import { computed, onMounted, ref } from "vue";

const modules = ref([]);
const isLoading = ref(true);
const userProgress = ref(0);
const showScormIframe = ref(false);
const scormIframeUrl = ref("");
const isScormIframeLoading = ref(false);
const isLoadingScormUrl = ref(false);
const isDownloadingCertificate = ref(false);

const progressStrokeWidth = 40;
const progressContainerSize = 328;

const averageProgress = computed(() => {
  return userProgress.value;
});

const circleRadius = computed(() => {
  return (progressContainerSize - progressStrokeWidth) / 2;
});

const circumference = computed(() => {
  return 2 * Math.PI * circleRadius.value;
});

const progressOffset = computed(() => {
  const percentage = averageProgress.value / 100;
  return circumference.value * (1 - percentage);
});

const isModuleDisabled = (index) => {
  if (index === 0) {
    return false;
  }
  const previousModule = modules.value[index - 1];
  return !previousModule || previousModule.progress < 100;
};

const fetchTrainerAcademies = async () => {
  try {
    isLoading.value = true;
    const response = await trainerAcademyService.getTrainerAcademies({
      page: 1,
      limit: 100,
    });

    if (response.data?.success && response.data?.data?.rows) {
      const filteredRows = response.data.data.rows
        .filter((row) => row.status === "published")
        .sort((a, b) => a.order - b.order);

      modules.value = filteredRows.map((row) => ({
        id: row.id,
        title: row.title,
        progress: row.completed || 0,
      }));
    }
  } catch (error) {
    console.error("Error fetching trainer academies:", getApiError(error));
  } finally {
    isLoading.value = false;
  }
};

const fetchMyProgress = async () => {
  try {
    const response = await trainerAcademyService.getMyProgress();

    if (response.data?.success && response.data?.data) {
      userProgress.value = response.data.data.progress || 0;

      if (response.data.data.academy_progresses && modules.value.length > 0) {
        const academyProgresses = response.data.data.academy_progresses;

        modules.value = modules.value.map((module) => {
          const matchingProgress = academyProgresses.find(
            (ap) => ap.title === module.title
          );

          if (matchingProgress) {
            return {
              ...module,
              progress: matchingProgress.progress || 0,
            };
          }

          return module;
        });
      }
    }
  } catch (error) {
    console.error("Error fetching progress:", getApiError(error));
  }
};

const viewModule = async (moduleId) => {
  if (!moduleId) {
    console.error("Cannot view module: Module ID not available.");
    return;
  }

  isLoadingScormUrl.value = true;
  try {
    const response =
      await trainerAcademyService.getTrainerAcademyView(moduleId);
    const url = response.data?.data?.url;
    if (url) {
      scormIframeUrl.value = url;
      showScormIframe.value = true;
      isScormIframeLoading.value = true;
    }
  } catch (error) {
    console.error("Error loading SCORM URL:", getApiError(error));
  } finally {
    isLoadingScormUrl.value = false;
  }
};

const scormIframeLoaded = () => {
  isScormIframeLoading.value = false;
};

const closeScormIframe = async () => {
  showScormIframe.value = false;
  scormIframeUrl.value = "";

  await fetchTrainerAcademies();
  await fetchMyProgress();
};

const downloadCertificate = async () => {
  try {
    isDownloadingCertificate.value = true;
    const response = await trainerAcademyService.getCertificate();

    let filename = "certificate.pdf";

    const contentDisposition =
      response.headers?.["content-disposition"] ||
      response.headers?.["Content-Disposition"];

    if (contentDisposition) {
      const patterns = [
        /filename\*=([^;]+)/i,
        /filename\*="([^"]+)"/i,
        /filename\*='([^']+)'/i,
        /filename="([^"]+)"/i,
        /filename='([^']+)'/i,
        /attachment;\s*filename\*="([^"]+)"/i,
        /attachment;\s*filename\*='([^']+)'/i,
        /attachment;\s*filename="([^"]+)"/i,
        /attachment;\s*filename='([^']+)'/i,
        /filename=([^;\s]+)/i,
        /attachment;\s*filename=([^;\s]+)/i,
      ];

      for (const pattern of patterns) {
        const match = contentDisposition.match(pattern);
        if (match && match[1]) {
          filename = match[1].trim();

          if (filename.includes("''")) {
            const parts = filename.split("''");
            if (parts.length > 1) {
              filename = decodeURIComponent(parts[1]);
            }
          } else {
            filename = filename.replace(/^["']|["']$/g, "");
          }

          break;
        }
      }
    } else {
      const userData = cookiesService.getCookie("authToken");
      if (userData?.fullname) {
        const sanitizedName = userData.fullname
          .replace(/[^a-zA-Z0-9\s-]/g, "")
          .trim()
          .replace(/\s+/g, " ");
        filename = `${sanitizedName}-trainer_certificate.pdf`;
      } else if (userData?.firstname && userData?.lastname) {
        const firstName = userData.firstname
          .replace(/[^a-zA-Z0-9\s-]/g, "")
          .trim();
        const lastName = userData.lastname
          .replace(/[^a-zA-Z0-9\s-]/g, "")
          .trim();
        filename = `${firstName} ${lastName}-trainer_certificate.pdf`;
      }
    }

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading certificate:", getApiError(error));
  } finally {
    isDownloadingCertificate.value = false;
  }
};

const handleContinueButtonClick = () => {
  if (modules.value.length === 0) return;

  if (averageProgress.value === 100) {
    downloadCertificate();
    return;
  }

  if (averageProgress.value === 0) {
    const firstModule = modules.value[0];
    if (firstModule?.id) {
      viewModule(firstModule.id);
    }
  } else {
    const uncompletedModule = modules.value.find(
      (module) => module.progress < 100
    );
    if (uncompletedModule?.id) {
      viewModule(uncompletedModule.id);
    }
  }
};

onMounted(async () => {
  await fetchTrainerAcademies();
  await fetchMyProgress();
});
</script>

<style scoped>
.content-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: stretch;
  justify-content: center;
  padding: 32px;
  margin: 0 auto;
}

.content-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  justify-content: center;
}

.congratulations-banner {
  background: var(--green-dark-1);
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  padding: 16px;
  border-radius: 16px;
  width: 100%;
}

.certificate-notice {
  display: block;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.024px;
}
.banner-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  font-weight: 400;
  justify-content: start;
  align-items: start;
  min-height: 1px;
  min-width: 1px;
  position: relative;
  flex-shrink: 0;
}

.banner-text {
  font-size: var(--fs-xl);
  line-height: 32px;
  color: white;
  margin: 0;
  font-weight: 400;
}

@media (max-width: 768px) {
  .banner-text {
    font-size: var(--fs-small);
    line-height: 24px;
  }

  .congratulations-banner {
    padding: 12px;
    gap: 12px;
  }

  .banner-icon {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 480px) {
  .banner-text {
    line-height: 24px;
  }

  .congratulations-banner {
    padding: 10px;
  }

  .banner-icon {
    width: 18px;
    height: 18px;
  }
}

.course-description {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px 0;
}

.description-text {
  font-family: "League Spartan", sans-serif;
  color: #3b4648;
  margin: 0;
}

.text-regular {
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px; /* 140% */
  letter-spacing: 0.03px;
}

.text-large {
  font-weight: 500;
  font-size: var(--fs-large);
  line-height: 28px;
}

.continue-button {
  width: fit-content;
  padding: 10px 24px;
}

.progress-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 0;
}

.progress-info {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8.747px;
  width: 328px;
  height: 328px;
  padding: 99.493px 123.547px;
}

.progress-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.progress-percentage {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 36px;
  line-height: 44px;
  color: #3b4648;
  margin: 0;
  z-index: 1;
}

.progress-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #6c737f;
  letter-spacing: 0.024px;
  text-align: center;
  margin: 0;
  z-index: 1;
}

.progress-circle {
  position: absolute;
  top: 0;
  left: 0;
  width: 328px;
  height: 328px;
}

.progress-svg {
  width: 100%;
  height: 100%;
}

.progress-ring {
  transition: stroke-dashoffset 0.5s ease;
}

.modules-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px 0;
  width: 524px;
}

.empty-state-container {
  padding: 32px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  text-align: center;
  background: white;
  border: 2px solid #d8dae5;
  border-radius: 12px;
  min-height: 400px;
}

.empty-state-icon {
  margin-bottom: 24px;
  opacity: 0.6;
}

.empty-state-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: #3b4648;
  margin: 0 0 16px 0;
  letter-spacing: 0.024px;
}

.empty-state-message {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #6c737f;
  margin: 0;
  max-width: 420px;
  letter-spacing: 0.024px;
}

.module-card {
  background: white;
  border: 2px solid #d8dae5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.module-card.module-active {
  border-color: #4c4ddc;
}

.module-card.module-completed {
  border-color: #4c4ddc;
}

.module-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.module-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: #3b4648;
}

.module-number {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.048px;
  margin: 0;
}

.module-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  letter-spacing: 0.024px;
  margin: 0;
}

.view-module-button {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  height: 40px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.view-module-button:hover:not(.button-disabled) {
  background-color: rgba(0, 61, 106, 0.05);
}

.button-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.014px;
  color: #003d6a;
}

.view-module-button.button-disabled .button-text {
  color: #cccccc;
}

.module-progress {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
}

.progress-bar-container {
  flex: 1;
  background-color: #bac1cc;
  height: 8px;
  border-radius: 1000000px;
  overflow: hidden;
}

.progress-bar-fill {
  background-color: #4c4ddc;
  height: 100%;
  border-radius: 1000000px;
  transition: width 0.5s ease;
}

.progress-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  letter-spacing: 0.035px;
  white-space: nowrap;
  margin: 0;
}

@media (max-width: 1200px) {
  .content-row {
    flex-direction: column;
    align-items: stretch;
  }

  .modules-container {
    width: 100%;
  }

  .progress-info {
    width: 280px;
    height: 280px;
    padding: 85px 105px;
  }

  .progress-circle {
    width: 280px;
    height: 280px;
  }
}

@media (max-width: 768px) {
  .content-section {
    padding: 16px;
    gap: 0px;
  }

  .course-description {
    padding: 24px 0;
    gap: 16px;
  }

  .content-row {
    gap: 0px;
  }

  .progress-container {
    padding: 24px 0;
  }

  .progress-info {
    width: 200px;
    height: 200px;
    padding: 60px 75px;
    gap: 0px;
  }

  .progress-circle {
    width: 200px;
    height: 200px;
  }

  .progress-percentage {
    font-size: var(--fs-medium);
  }

  .progress-label {
    font-size: 12px;
    line-height: 16px;
  }

  .progress-icon {
    width: 24px;
    height: 24px;
  }

  .modules-container {
    padding: 24px 0;
  }

  .empty-state {
    padding: 48px 24px;
    min-height: 300px;
  }

  .empty-state-title {
    font-size: var(--fs-big);
    line-height: 28px;
  }

  .empty-state-message {
    font-size: var(--fs-small);
    line-height: 20px;
  }

  .continue-button {
    width: 100%;
  }
}

.scorm-iframe-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.scorm-iframe-container {
  width: 100%;
  height: 100%;
  background-color: white;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

.scorm-iframe-header {
  display: flex;
  justify-content: flex-end;
  position: absolute;
  top: 0;
  left: 0;
  right: 20px;
  padding: 10px;
  z-index: 1000;
  background-color: transparent;
}

.scorm-iframe-modal iframe {
  flex: 1;
  width: 100%;
  height: 100%;
  border: none;
}

.scorm-iframe-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 5;
}

.scorm-iframe-loading p {
  margin-top: 15px;
  color: #333;
  font-weight: 500;
}

.scorm-loader {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid var(--primary-blue, #1976d2);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
